function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container, options) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  var focusOptions = options && options.preventScroll ? { preventScroll: true } : undefined;
  elementToFocus.focus(focusOptions);

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus({ preventScroll: true });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not([data-search-trigger]):not(.reset__button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (elementToFocus) {
      elementToFocus.setAttribute('aria-expanded', 'false');
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    
    this.initDesktopExpansion();
    window.addEventListener('resize', this.handleResizeForExpansion.bind(this));
  }

  initDesktopExpansion() {
    if (this.isDesktop()) {
      this.expandAllSubmenus();
    }
  }

  isDesktop() {
    return window.innerWidth >= 990;
  }

  expandAllSubmenus() {
    const submenus = this.mainDetailsToggle.querySelectorAll('details');
    submenus.forEach(details => {
      if (details !== this.mainDetailsToggle) {
        details.setAttribute('open', '');
        const summary = details.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
        }
      }
    });
  }

  collapseAllSubmenus() {
    const submenus = this.mainDetailsToggle.querySelectorAll('details');
    submenus.forEach(details => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        const summary = details.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  handleResizeForExpansion() {
    if (this.isDesktop()) {
      this.expandAllSubmenus();
    } else {
      this.collapseAllSubmenus();
    }
  }

  openMenuDrawer(summaryElement) {
    this.closeAllOtherHeaderDrawers();
    
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    
    if (this.isDesktop()) {
      setTimeout(() => {
        this.expandAllSubmenus();
      }, 100);
    }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    if (this.header) {
      this.header.classList.remove('menu-open');
    }
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  closeAllOtherHeaderDrawers() {
    const allHeaderDrawers = document.querySelectorAll('header-drawer');
    allHeaderDrawers.forEach(drawer => {
      if (drawer === this) return;
      
      const detailsElement = drawer.querySelector('details[open]');
      if (detailsElement) {
        const summaryElement = detailsElement.querySelector('summary');
        summaryElement.setAttribute('aria-expanded', 'false');
        drawer.closeMenuDrawer(new Event('close'), summaryElement);
      }
    });

    const cartDrawers = document.querySelectorAll('cart-drawer');
    cartDrawers.forEach(cartDrawer => {
      if (cartDrawer.classList.contains('active')) {
        cartDrawer.close();
      }
    });

    document.querySelectorAll('header-panel-drawer').forEach(panelDrawer => {
      if (panelDrawer.classList.contains('active')) {
        panelDrawer.close();
      }
    });
  }
}

customElements.define('header-drawer', HeaderDrawer);

class MegaMenuHoverNav extends HTMLElement {
  static leaveDelay = 220;

  connectedCallback() {
    if (this._megaHoverInit) return;
    this._megaHoverInit = true;

    this.leaveTimer = null;
    this._megaHoverCollapseFallbackTimer = null;
    this._megaHoverCollapsing = false;
    this._megaHoverHeightEndHandler = null;
    this._megaHoverLayoutRaf = null;
    this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.boundOnNavMouseEnter = this.onNavMouseEnter.bind(this);
    this.boundOnNavMouseLeave = this.onNavMouseLeave.bind(this);
    this.boundOnMediaChange = this.onMediaChange.bind(this);
    this.boundUpdatePanelsRootLayout = this.updatePanelsRootLayout.bind(this);
    this.boundSchedulePanelsRootLayoutUpdate = this.schedulePanelsRootLayoutUpdate.bind(this);

    this.mediaQuery = window.matchMedia('(min-width: 990px)');

    window.addEventListener('resize', this.boundUpdatePanelsRootLayout);

    this.querySelectorAll('[data-mega-hover-trigger]').forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        if (!this.mediaQuery.matches) return;
        const idx = parseInt(trigger.dataset.megaHoverIndex, 10);
        if (!Number.isNaN(idx)) this.openPanel(idx);
      });
      trigger.addEventListener('focus', () => {
        if (!this.mediaQuery.matches) return;
        const idx = parseInt(trigger.dataset.megaHoverIndex, 10);
        if (!Number.isNaN(idx)) this.openPanel(idx);
      });
    });

    this.addEventListener('mouseenter', this.boundOnNavMouseEnter);
    this.addEventListener('mouseleave', this.boundOnNavMouseLeave);
    document.addEventListener('keydown', this.boundOnDocumentKeydown);
    this.mediaQuery.addEventListener('change', this.boundOnMediaChange);

    this.updatePanelsRootLayout();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundOnDocumentKeydown);
    this.removeEventListener('mouseenter', this.boundOnNavMouseEnter);
    this.removeEventListener('mouseleave', this.boundOnNavMouseLeave);
    window.removeEventListener('resize', this.boundUpdatePanelsRootLayout);
    this.cancelPanelsRootLayoutUpdate();
    this.cancelHeightAnimation();
    if (this._megaHoverScrollBound) {
      window.removeEventListener('scroll', this.boundSchedulePanelsRootLayoutUpdate);
      this._megaHoverScrollBound = false;
    }
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.boundOnMediaChange);
    }
  }

  prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  getPanelsRoot() {
    return this.querySelector('.mega-menu-hover__panels-root');
  }

  getActivePanel() {
    return this.querySelector('.mega-menu-hover__panel.mega-menu-hover__panel--active');
  }

  schedulePanelsRootLayoutUpdate() {
    if (!this.mediaQuery?.matches) return;
    if (this._megaHoverLayoutRaf) return;

    this._megaHoverLayoutRaf = requestAnimationFrame(() => {
      this._megaHoverLayoutRaf = null;
      this.updatePanelsRootLayout();
    });
  }

  cancelPanelsRootLayoutUpdate() {
    if (!this._megaHoverLayoutRaf) return;
    cancelAnimationFrame(this._megaHoverLayoutRaf);
    this._megaHoverLayoutRaf = null;
  }

  cancelHeightAnimation() {
    const root = this.getPanelsRoot();
    if (root && this._megaHoverHeightEndHandler) {
      root.removeEventListener('transitionend', this._megaHoverHeightEndHandler);
    }
    this._megaHoverHeightEndHandler = null;
    if (this._megaHoverCollapseFallbackTimer) {
      clearTimeout(this._megaHoverCollapseFallbackTimer);
      this._megaHoverCollapseFallbackTimer = null;
    }
    this._megaHoverCollapsing = false;
  }

  animatePanelsHeightTo(targetPx, { setAutoOnEnd = false } = {}) {
    const root = this.getPanelsRoot();
    if (!root) return;

    this.cancelHeightAnimation();

    if (this.prefersReducedMotion()) {
      root.style.height = setAutoOnEnd ? 'auto' : `${targetPx}px`;
      return;
    }

    const startPx = Math.max(0, Math.round(root.getBoundingClientRect().height || 0));
    root.style.height = `${startPx}px`;
    root.offsetHeight;
    root.style.height = `${Math.max(0, Math.round(targetPx))}px`;

    this._megaHoverHeightEndHandler = (event) => {
      if (event.target !== root || event.propertyName !== 'height') return;
      root.removeEventListener('transitionend', this._megaHoverHeightEndHandler);
      this._megaHoverHeightEndHandler = null;
      if (!this.hasAttribute('data-open')) return;
      if (setAutoOnEnd) root.style.height = 'auto';
    };
    root.addEventListener('transitionend', this._megaHoverHeightEndHandler);
  }

  updatePanelsRootLayout() {
    const root = this.querySelector('.mega-menu-hover__panels-root');
    if (!root || !this.mediaQuery.matches) return;

    const headerEl =
      this.closest('sticky-header') ||
      document.querySelector('sticky-header') ||
      this.closest('.section-header') ||
      document.querySelector('.section-header');

    if (headerEl && typeof headerEl.getBoundingClientRect === 'function') {
      const rect = headerEl.getBoundingClientRect();
      root.style.top = `${Math.max(0, Math.round(rect.bottom || 0))}px`;
      return;
    }

    const headerHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    const headerHeight = parseFloat(headerHeightVar) || 0;
    const promoHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--promo-banner-height').trim();
    const promoHeight = parseFloat(promoHeightVar) || 0;
    root.style.top = `${headerHeight + promoHeight}px`;
  }

  onNavMouseEnter() {
    clearTimeout(this.leaveTimer);
  }

  onNavMouseLeave() {
    this.leaveTimer = setTimeout(() => {
      if (this.mediaQuery.matches) this.close();
    }, MegaMenuHoverNav.leaveDelay);
  }

  onDocumentKeydown(event) {
    if (event.code !== 'Escape' || !this.hasAttribute('data-open')) return;
    this.close();
  }

  onMediaChange() {
    if (!this.mediaQuery.matches) this.close();
    this.updatePanelsRootLayout();
  }

  openPanel(index) {
    clearTimeout(this.leaveTimer);
    const wasOpen = this.hasAttribute('data-open');
    this.cancelHeightAnimation();
    this.setAttribute('data-open', 'true');

    this.updatePanelsRootLayout();
    if (!this._megaHoverScrollBound) {
      window.addEventListener('scroll', this.boundSchedulePanelsRootLayoutUpdate, { passive: true });
      this._megaHoverScrollBound = true;
    }

    const panels = this.querySelectorAll('.mega-menu-hover__panel');
    const triggers = this.querySelectorAll('[data-mega-hover-trigger]');

    panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('mega-menu-hover__panel--active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    triggers.forEach((trigger) => {
      const idx = parseInt(trigger.dataset.megaHoverIndex, 10);
      trigger.setAttribute('aria-expanded', idx === index ? 'true' : 'false');
    });

    const root = this.getPanelsRoot();
    const activePanel = this.getActivePanel();
    if (root && activePanel) {
      const targetHeight = Math.max(0, Math.round(activePanel.scrollHeight || 0));
      if (!wasOpen) root.style.height = '0px';
      this.animatePanelsHeightTo(targetHeight, { setAutoOnEnd: true });
    }

    const header = document.querySelector('.header');
    if (header) header.classList.add('menu-open');
  }

  close() {
    clearTimeout(this.leaveTimer);
    const root = this.getPanelsRoot();
    if (!this.hasAttribute('data-open')) return;

    this.cancelHeightAnimation();

    if (!root || this.prefersReducedMotion()) {
      this.finalizeClose();
      if (root) root.style.height = '0px';
      return;
    }

    this._megaHoverCollapsing = true;
    const currentHeight = Math.max(0, Math.round(root.getBoundingClientRect().height || 0));
    root.style.height = `${currentHeight}px`;
    root.offsetHeight;
    requestAnimationFrame(() => {
      root.style.height = '0px';
    });

    const endHandler = (event) => {
      if (event.target !== root || event.propertyName !== 'height') return;
      root.removeEventListener('transitionend', endHandler);
      this._megaHoverHeightEndHandler = null;
      this._megaHoverCollapsing = false;
      this.finalizeClose();
    };
    this._megaHoverHeightEndHandler = endHandler;
    root.addEventListener('transitionend', endHandler);

    this._megaHoverCollapseFallbackTimer = setTimeout(() => {
      if (!this._megaHoverCollapsing) return;
      root.removeEventListener('transitionend', endHandler);
      this._megaHoverHeightEndHandler = null;
      this._megaHoverCollapsing = false;
      this.finalizeClose();
    }, 400);
  }

  finalizeClose() {
    this.removeAttribute('data-open');

    if (this._megaHoverScrollBound) {
      window.removeEventListener('scroll', this.boundSchedulePanelsRootLayoutUpdate);
      this._megaHoverScrollBound = false;
    }
    this.cancelPanelsRootLayoutUpdate();

    this.querySelectorAll('.mega-menu-hover__panel').forEach((panel) => {
      panel.classList.remove('mega-menu-hover__panel--active');
      panel.setAttribute('aria-hidden', 'true');
    });

    this.querySelectorAll('[data-mega-hover-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    const header = document.querySelector('.header');
    if (header) header.classList.remove('menu-open');
  }
}

customElements.define('mega-menu-hover-nav', MegaMenuHoverNav);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.isAndroid = /Android/i.test(navigator.userAgent);
    
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    
    this.offsetHeight;
    
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    window.pauseAllMedia();
    
    requestAnimationFrame(() => {
      const content = this.querySelector('.product-popup-modal__content');
      if (content) {
        content.offsetHeight;
      }
      trapFocus(this, this.querySelector('[role="dialog"]'), { preventScroll: true });
    });
  }

  hide() {
    // Add closing class to trigger CSS animation
    this.classList.add('modal-closing');
    
    // Wait for animation to complete before removing
    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden-desktop');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      this.classList.remove('modal-closing');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
    }, 500); // Match the CSS transition duration (--duration-long)
  }
}
customElements.define('modal-dialog', ModalDialog);

class InlineModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.isAndroid = /Android/i.test(navigator.userAgent);
    
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      
      const targetContainer = document.querySelector('.inline-modal-container');
      
      if (targetContainer) {
        targetContainer.insertBefore(this, targetContainer.firstChild);
      }
      
      const section = this.closest('.shopify-section');
      if (section) {
        this.dataset.section = section.id.replace('shopify-section-', '');
      }
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    
    const modalOpener = this.closest('modal-opener');
    if (modalOpener) {
      modalOpener.classList.add('modal-active');
    } else {
      document.body.classList.add('overflow-hidden');
    }
    
    this.offsetHeight;
    
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'), { preventScroll: true });
    window.pauseAllMedia();
    
    requestAnimationFrame(() => {
      const content = this.querySelector('.product-popup-modal__content');
      if (content) {
        content.offsetHeight;
      }
    });
  }

  hide() {
    const modalOpener = this.closest('modal-opener');
    
    // Add closing class to trigger CSS animation
    this.classList.add('modal-closing');
    
    // Wait for animation to complete before removing
    setTimeout(() => {
      if (modalOpener) {
        modalOpener.classList.remove('modal-active');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
      
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      this.classList.remove('modal-closing');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
    }, 500); // Match the CSS transition duration (--duration-long)
  }
}

customElements.define('inline-modal-dialog', InlineModalDialog);


class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {});
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      // If this opener lives inside an open header drawer (menu drawer),
      // close the drawer first so it can remove `overflow-hidden-{breakpoint}`
      // (e.g. `overflow-hidden-tablet`) before the modal locks scroll.
      const headerDrawer = this.closest('header-drawer');
      if (headerDrawer) {
        const openDetails = headerDrawer.querySelector('details[open]');
        const summaryElement = openDetails && openDetails.querySelector('summary');
        if (summaryElement && typeof headerDrawer.closeMenuDrawer === 'function') {
          headerDrawer.closeMenuDrawer(new Event('close'), summaryElement);
        }
      }

      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

// Subscribe/Buy intent handler (global)
// - Subscribe: set Roast Type to "Subscription" (if exists)
// - Buy: reset Roast Type to first available option
(function () {
  if (window.__xbloomIntentRoastTypeHandlerInit) return;
  window.__xbloomIntentRoastTypeHandlerInit = true;

  const ROAST_TYPE_LABEL = 'Roast Type';
  const SUBSCRIBE_VALUE = 'Subscription';
  const MAX_ATTEMPTS = 40;
  const INTERVAL_MS = 50;

  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();

  const isRadioAvailable = (radio) => {
    if (!radio) return false;
    if (radio.disabled) return false;
    if (radio.classList.contains('visually-disabled')) return false;
    if (radio.classList.contains('disabled')) return false;
    return true;
  };

  const selectRadio = (root, radio) => {
    if (!radio) return false;
    if (radio.checked) return true;
    // Prefer programmatic selection + change for reliability in modals.
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const findRoastTypeFieldset = (root) => {
    const fieldsets = Array.from(root.querySelectorAll('fieldset.js.product-form__input'));
    return fieldsets.find((fs) => normalize(fs.querySelector('legend')?.textContent).includes(ROAST_TYPE_LABEL));
  };

  const setOptionAndDispatchChange = (optionWrapper, value) => {
    if (!optionWrapper || value == null || value === '') return false;
    const select = optionWrapper.querySelector('select');
    if (select) {
      if (select.value === value) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    const radio = optionWrapper.querySelector('input[type="radio"][value="' + String(value).replace(/"/g, '\\"') + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  };

  const applyPDPIntent = (modal, intent) => {
    const section = modal.querySelector('[id^="ProductInfo-"]');
    const form = modal.querySelector('product-form form') || modal.querySelector('form[data-type="add-to-cart-form"]');
    if (!section || !form) return;
    const sellingPlanInput = form.querySelector('input[name="selling_plan"], input.selling-plan-input');
    const priceContainer = modal.querySelector('.price-container_modal');
    const oneTimeBlock = priceContainer ? priceContainer.querySelector('.price-block--one-time') : null;
    const subscriptionBlock = priceContainer ? priceContainer.querySelector('.price-block--subscription') : null;

    if (intent === 'subscribe') {
      const planId = section.getAttribute('data-plan-id-2w');
      const subscriptionVariantId = section.getAttribute('data-subscription-variant-id');
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput && subscriptionVariantId) {
        variantIdInput.value = subscriptionVariantId;
        variantIdInput.disabled = false;
      }
      if (sellingPlanInput) sellingPlanInput.value = planId != null ? planId : '';
      modal.setAttribute('data-pending-subscribe-intent', '1');
      const roastTypeFieldset = findRoastTypeFieldset(modal);
      if (roastTypeFieldset) {
        const radios = roastTypeFieldset.querySelectorAll('input[type="radio"]');
        const subRadio = Array.from(radios).find((r) => normalize(r.value) === SUBSCRIBE_VALUE);
        if (subRadio) selectRadio(modal, subRadio);
      }
      if (oneTimeBlock) oneTimeBlock.style.display = 'none';
      if (subscriptionBlock) subscriptionBlock.style.display = '';
    } else {
      const values = [
        section.getAttribute('data-option-1-value'),
        section.getAttribute('data-option-2-value'),
        section.getAttribute('data-option-3-value')
      ];
      const variantSelects = form.querySelector('variant-selects');
      if (variantSelects) {
        const wrappers = variantSelects.querySelectorAll('.product-form__input');
        for (let i = 0; i < wrappers.length && i < values.length; i++) {
          const value = values[i];
          if (value != null && value !== '') setOptionAndDispatchChange(wrappers[i], value);
        }
      }
      if (sellingPlanInput) sellingPlanInput.value = '';
      if (oneTimeBlock) oneTimeBlock.style.display = '';
      if (subscriptionBlock) subscriptionBlock.style.display = 'none';
    }
  };

  const applyRoastTypeIntent = (modal, intent) => {
    // Prefer radio (pills/swatches)
    const roastTypeFieldset = findRoastTypeFieldset(modal);
    if (roastTypeFieldset) {
      const radios = Array.from(roastTypeFieldset.querySelectorAll('input[type="radio"]'));

      if (intent === 'subscribe') {
        const desired = radios.find((r) => normalize(r.value) === SUBSCRIBE_VALUE);
        // In quick-add modals, option rendering/availability can lag.
        // If not found yet, keep retrying for a short window.
        if (!desired) return false;
        // For Subscribe intent, select Subscription if it exists (even if visually-disabled).
        if (desired.disabled) return true;
        return selectRadio(modal, desired);
      }

      if (intent === 'buy') {
        const firstAvailable = radios.find(isRadioAvailable);
        if (!firstAvailable) return false;
        return selectRadio(modal, firstAvailable);
      }
    }

    // Fallback: select
    const select = modal.querySelector('select[name="options[Roast Type]"]');
    if (!select) return false;

    if (intent === 'subscribe') {
      const desiredOpt = Array.from(select.options || []).find(
        (opt) => normalize(opt.value) === SUBSCRIBE_VALUE || normalize(opt.textContent) === SUBSCRIBE_VALUE
      );
      if (!desiredOpt) return true; // No Subscription option: do nothing
      if (select.value !== desiredOpt.value) {
        select.value = desiredOpt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    if (intent === 'buy') {
      const firstAvailable = Array.from(select.options || []).find((opt) => !opt.disabled);
      if (!firstAvailable) return false;
      if (select.value !== firstAvailable.value) {
        select.value = firstAvailable.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    return false;
  };

  const waitForModalAndApply = (modal, intent) => {
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      if (!modal.hasAttribute('open')) {
        if (attempts < MAX_ATTEMPTS) setTimeout(tick, INTERVAL_MS);
        return;
      }

      // QuickAdd modals inject content asynchronously; wait for variant UI to exist.
      const hasVariantUI =
        !!modal.querySelector('variant-selects') ||
        !!modal.querySelector('fieldset.js.product-form__input') ||
        !!modal.querySelector('select[name="options[Roast Type]"]');
      if (!hasVariantUI) {
        if (attempts < MAX_ATTEMPTS) setTimeout(tick, INTERVAL_MS);
        return;
      }

      const applied = applyRoastTypeIntent(modal, intent);
      if (modal.tagName === 'INLINE-MODAL-DIALOG') {
        applyPDPIntent(modal, intent);
      }
      if (!applied && attempts < MAX_ATTEMPTS) {
        setTimeout(tick, INTERVAL_MS);
      }
    };

    setTimeout(tick, 0);
  };

  // After product-info replaces DOM on variant change, re-apply subscription state for PDP Subscribe intent
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const modal = document.querySelector('[data-pending-subscribe-intent]');
      if (!modal || !data?.sectionId) return;
      const productInfo = modal.closest('product-info') || modal.querySelector('product-info');
      const section = modal.querySelector('[id^="ProductInfo-"]');
      if (!section || !productInfo || productInfo.dataset.section !== String(data.sectionId)) return;
      const form = modal.querySelector('product-form form') || modal.querySelector('form[data-type="add-to-cart-form"]');
      const planId = section.getAttribute('data-plan-id-2w');
      const sellingPlanInput = form?.querySelector('input[name="selling_plan"], input.selling-plan-input');
      if (sellingPlanInput && planId != null) sellingPlanInput.value = planId;
      const priceContainer = modal.querySelector('.price-container_modal');
      const oneTimeBlock = priceContainer?.querySelector('.price-block--one-time');
      const subscriptionBlock = priceContainer?.querySelector('.price-block--subscription');
      if (oneTimeBlock) oneTimeBlock.style.display = 'none';
      if (subscriptionBlock) subscriptionBlock.style.display = '';
      modal.removeAttribute('data-pending-subscribe-intent');
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[data-open-intent="subscribe"], button[data-open-intent="buy"]');
      if (!button) return;

      const intent = normalize(button.getAttribute('data-open-intent'));
      if (intent !== 'subscribe' && intent !== 'buy') return;

      const opener = button.closest('modal-opener');
      const modalSelector = opener?.getAttribute('data-modal');
      if (!modalSelector) return;

      const modal = document.querySelector(modalSelector);
      if (!modal) return;

      // PDP inline modal: apply intent synchronously BEFORE the modal opens (we're in capture phase).
      // Subscribe: set variant id, selling_plan, flag modal, then dispatch change so Roast Type + shipping_time
      // update; variantChange handler re-applies subscription price block after product-info replaces DOM.
      // Buy: set options (with dispatch) and price block.
      if (modal.tagName === 'INLINE-MODAL-DIALOG') {
        const hasVariantUI =
          !!modal.querySelector('variant-selects') ||
          !!modal.querySelector('fieldset.js.product-form__input') ||
          !!modal.querySelector('select[name="options[Roast Type]"]');
        if (hasVariantUI) {
          if (intent === 'subscribe') {
            applyPDPIntent(modal, intent);
          } else {
            applyRoastTypeIntent(modal, intent);
            applyPDPIntent(modal, intent);
          }
        }
        return;
      }

      waitForModalAndApply(modal, intent);
    },
    { capture: true, passive: true }
  );

  // Expose for Quick Add to call after content is loaded (list page Subscribe/Buy).
  window.__xbloomApplyRoastTypeIntent = applyRoastTypeIntent;
})();

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    this.progressIndicator = this.querySelector('[data-progress-indicator]');
    this.remainingMs = null;
    this.progressOffset = 100;
    this.autoplayGuard = false;
    this.isTogglingAutoplay = false;
    this.previousPage = this.currentPage || 1;

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();

    // Initialize drag functionality for desktop only (not touch devices)
    this.initDragToSlide();
    
    // Initialize mobile touch handling for touch devices
    this.initMobileTouchHandling();

    if (this.progressIndicator) {
      this.onProgressEnd = (e) => {
        if (e.propertyName !== 'stroke-dashoffset') return;
        if (this.autoplayGuard) return;
        if (this.autoplayButtonIsSetToPlay && this.sliderItemsToShow.length > 1 && this.autoplay) {
          this.autoplayGuard = true;
          clearInterval(this.autoplay);
          this.autoplay = null;
          this.autoRotateSlides();
        }
      };
      this.progressIndicator.addEventListener('transitionend', this.onProgressEnd);
    }
  }

  isFadeEffect() {
    return this.slider?.classList.contains('slideshow--effect-fade');
  }

  initPages() {
    if (!this.slider || !this.isFadeEffect()) {
      super.initPages();
      return;
    }

    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (!this.sliderItemsToShow.length) return;

    this.sliderItemOffset = this.slider.clientWidth || this.sliderItemsToShow[0].clientWidth || 1;
    this.slidesPerPage = 1;
    this.totalPages = this.sliderItemsToShow.length;
    this.currentPage = Math.min(Math.max(this.currentPage || 1, 1), this.totalPages);
    this.update();
  }

  resetSlideshowProgress() {
    this.remainingMs = null;
    this.progressOffset = 100;

    if (!this.progressIndicator) return;

    this.progressIndicator.style.transition = 'none';
    this.progressIndicator.style.strokeDasharray = '100';
    this.progressIndicator.style.strokeDashoffset = '100';
    void this.progressIndicator.offsetWidth;
  }

  updateFadeSlides() {
    if (!this.isFadeEffect() || !this.sliderItemsToShow?.length) return;

    this.slider.classList.add('slideshow--fade-ready');
    this.sliderItemsToShow.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.currentPage - 1);
    });
  }

  setFadePage(pageNumber) {
    if (!this.isFadeEffect() || !this.sliderItemsToShow?.length) return;

    const totalPages = this.sliderItemsToShow.length;
    const previousPage = this.currentPage || 1;
    let nextPage = Number(pageNumber) || 1;

    if (this.enableSliderLooping) {
      nextPage = ((nextPage - 1) % totalPages + totalPages) % totalPages + 1;
    } else {
      nextPage = Math.min(Math.max(nextPage, 1), totalPages);
    }

    this.currentPage = nextPage;
    this.update();
    this.previousPage = nextPage;
    this.setSlideVisibility();
    this.slider.dispatchEvent(new Event('scroll'));

    if (nextPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }
  }

  connectedCallback() {
    if (this._xbloomSlideshowVideoBound || !this.classList.contains('xbloom-slideshow-component')) return;
    const slider = this.querySelector('[id^="Slider-"]');
    if (!slider || !this.querySelector('.slideshow__media-video video')) return;
    this._xbloomSlideshowVideoBound = true;

    const syncSlideshowVideos = () => {
      const slides = Array.from(this.querySelectorAll('.slideshow__slide')).filter((el) => el.clientWidth > 0);
      if (!slides.length) return;
      let activeIndex = 0;
      if (this.isFadeEffect()) {
        const activeSlideIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        activeIndex = activeSlideIndex >= 0 ? activeSlideIndex : 0;
      } else if (slides.length > 1) {
        const offset = slides[1].offsetLeft - slides[0].offsetLeft;
        if (offset > 0) {
          activeIndex = Math.round(slider.scrollLeft / offset);
          activeIndex = Math.max(0, Math.min(slides.length - 1, activeIndex));
        }
      }
      slides.forEach((slide, i) => {
        const video = slide.querySelector('.slideshow__media-video video');
        if (!video) return;
        if (i === activeIndex) {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        } else {
          video.pause();
        }
      });
    };

    slider.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(syncSlideshowVideos);
      },
      { passive: true }
    );
    document.body.addEventListener('modalClosed', syncSlideshowVideos);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncSlideshowVideos();
    });
    requestAnimationFrame(syncSlideshowVideos);
  }

  initDragToSlide() {
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      return;
    }

    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.wasAutoPlaying = false;

    this.slider.style.userSelect = 'none';
    this.slider.style.cursor = 'grab';

    this.slider.addEventListener('mousedown', this.onDragStart.bind(this));
    document.addEventListener('mousemove', this.onDragMove.bind(this));
    document.addEventListener('mouseup', this.onDragEnd.bind(this));
    this.slider.addEventListener('dragstart', (e) => e.preventDefault());
  }

  initMobileTouchHandling() {
    // Only initialize for touch devices
    if (!('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      return;
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartPage = 1;
    this.isTouching = false;
    this.touchWasAutoPlaying = false;

    this.slider.addEventListener('touchstart', this.onMobileTouchStart.bind(this), { passive: true });
    this.slider.addEventListener('touchmove', this.onMobileTouchMove.bind(this), { passive: false });
    this.slider.addEventListener('touchend', this.onMobileTouchEnd.bind(this), { passive: true });
  }

  onMobileTouchStart(event) {
    // Ignore if touching buttons or links
    if (
      event.target.closest('button') ||
      event.target.closest('a') ||
      event.target.closest('.slider-counter__link') ||
      event.target.closest('.slideshow__autoplay')
    ) {
      return;
    }

    this.isTouching = true;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartPage = this.currentPage;

    // Pause autoplay during touch
    const shouldBePlaying = this.sliderAutoplayButton
      ? this.autoplayButtonIsSetToPlay
      : !this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked;
    
    if (shouldBePlaying && this.autoplay) {
      this.touchWasAutoPlaying = true;
      this.pause();
    } else {
      this.touchWasAutoPlaying = false;
    }
  }

  onMobileTouchMove(event) {
    if (!this.isTouching) return;

    // Allow native scrolling for vertical swipes
    const touchX = event.touches[0].clientX;
    const touchY = event.touches[0].clientY;
    const deltaX = Math.abs(touchX - this.touchStartX);
    const deltaY = Math.abs(touchY - this.touchStartY);

    // If vertical scroll is more dominant, allow default behavior
    if (deltaY > deltaX) {
      return;
    }

    // Prevent default for horizontal swipes to allow custom handling
    if (deltaX > 10) {
      event.preventDefault();
    }
  }

  onMobileTouchEnd(event) {
    if (!this.isTouching) return;

    this.isTouching = false;

    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;
    const deltaX = touchEndX - this.touchStartX;
    const deltaY = touchEndY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Only process horizontal swipes (ignore vertical scrolling)
    if (absDeltaX < absDeltaY || absDeltaX < 30) {
      // Restart autoplay if it was playing
      if (this.touchWasAutoPlaying) {
        setTimeout(() => {
          if (this.sliderAutoplayButton) {
            if (this.autoplayButtonIsSetToPlay) {
              this.play();
            }
          } else {
            if (!this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked) {
              this.play();
            }
          }
        }, 100);
      }
      return;
    }

    const threshold = this.slider.clientWidth * 0.15;
    const isFirstSlide = this.touchStartPage === 1;
    const isLastSlide = this.touchStartPage === this.sliderItemsToShow.length;

    // Handle swipe right (previous) - deltaX > 0
    if (deltaX > threshold) {
      if (isFirstSlide && this.enableSliderLooping) {
        // Loop to last slide
        const lastPage = this.sliderItemsToShow.length;
        this.snapToSlide(lastPage, 'previous');
      } else if (!isFirstSlide) {
        this.snapToSlide(this.touchStartPage - 1, 'previous');
      } else {
        this.snapToSlide(this.touchStartPage);
      }
    }
    // Handle swipe left (next) - deltaX < 0
    else if (deltaX < -threshold) {
      if (isLastSlide && this.enableSliderLooping) {
        // Loop to first slide
        this.snapToSlide(1, 'next');
      } else if (!isLastSlide) {
        this.snapToSlide(this.touchStartPage + 1, 'next');
      } else {
        this.snapToSlide(this.touchStartPage);
      }
    }

    // Restart autoplay if it was playing
    if (this.touchWasAutoPlaying) {
      setTimeout(() => {
        if (this.sliderAutoplayButton) {
          if (this.autoplayButtonIsSetToPlay) {
            this.play();
          }
        } else {
          if (!this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked) {
            this.play();
          }
        }
      }, 100);
    }
  }

  onDragStart(event) {
    if (
      event.target.closest('button') ||
      event.target.closest('a') ||
      event.target.closest('.slider-counter__link') ||
      event.target.closest('.slideshow__autoplay')
    ) {
      return;
    }

    this.isDragging = true;
    this.dragStartX = event.pageX;
    this.dragStartScrollLeft = this.slider.scrollLeft;
    this.dragStartPage = this.currentPage;
    this.slider.style.cursor = 'grabbing';
    this.slider.style.scrollBehavior = 'auto';

    const shouldBePlaying = this.sliderAutoplayButton
      ? this.autoplayButtonIsSetToPlay
      : !this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked;
    
    if (shouldBePlaying && this.autoplay) {
      this.wasAutoPlaying = true;
      this.pause();
    } else {
      this.wasAutoPlaying = false;
    }

    event.preventDefault();
  }

  onDragMove(event) {
    if (!this.isDragging) return;

    event.preventDefault();

    if (this.isFadeEffect()) return;

    const deltaX = event.pageX - this.dragStartX;
    const newScrollLeft = this.dragStartScrollLeft - deltaX;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    this.slider.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
  }

  onDragEnd(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.slider.style.cursor = 'grab';
    this.slider.style.scrollBehavior = '';

    const deltaX = event.pageX - this.dragStartX;
    const threshold = this.slider.clientWidth * 0.15;

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        this.goToPreviousSlideFromDrag();
      } else {
        this.goToNextSlideFromDrag();
      }
    } else {
      this.snapToSlide(this.dragStartPage);
    }

    this.remainingMs = null;
    this.progressOffset = 100;
    if (this.progressIndicator) {
      this.progressIndicator.style.transition = 'none';
      this.progressIndicator.style.strokeDasharray = '100';
      this.progressIndicator.style.strokeDashoffset = '100';
      void this.progressIndicator.offsetWidth;
    }

    const wasAutoPlayingBeforeDrag = this.wasAutoPlaying;
    this.wasAutoPlaying = false;
    
    if (wasAutoPlayingBeforeDrag) {
      setTimeout(() => {
        if (this.sliderAutoplayButton) {
          if (this.autoplayButtonIsSetToPlay) {
            this.play();
          }
        } else {
          if (!this.reducedMotion?.matches && !this.announcementBarArrowButtonWasClicked) {
            this.play();
          }
        }
      }, 100);
    }
  }

  goToPreviousSlideFromDrag() {
    const startPage = this.dragStartPage || this.currentPage;
    const isFirstSlide = startPage === 1;
    
    if (isFirstSlide && this.enableSliderLooping) {
      const lastPage = this.sliderItemsToShow.length;
      this.snapToSlide(lastPage, 'previous');
    } else if (!isFirstSlide) {
      this.snapToSlide(startPage - 1, 'previous');
    } else {
      this.snapToSlide(startPage);
    }
  }

  goToNextSlideFromDrag() {
    const startPage = this.dragStartPage || this.currentPage;
    const isLastSlide = startPage === this.sliderItemsToShow.length;

    if (isLastSlide && this.enableSliderLooping) {
      this.snapToSlide(1, 'next');
    } else if (!isLastSlide) {
      this.snapToSlide(startPage + 1, 'next');
    } else {
      this.snapToSlide(startPage);
    }
  }

  snapToSlide(pageNumber, direction = null) {
    if (this.isFadeEffect()) {
      this.setFadePage(pageNumber);

      if (direction && this.announcementBarSlider) {
        this.applyAnimationToAnnouncementBar(direction);
      }

      this.resetSlideshowProgress();

      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.startProgressBar();
      }
      return;
    }

    const slideIndex = pageNumber - 1;
    const targetScrollLeft = slideIndex * this.sliderItemOffset;
    
    this.setSlidePosition(targetScrollLeft);
    
    if (direction && this.announcementBarSlider) {
      this.applyAnimationToAnnouncementBar(direction);
    }
    
    this.remainingMs = null;
    this.progressOffset = 100;
    if (this.progressIndicator) {
      this.progressIndicator.style.transition = 'none';
      this.progressIndicator.style.strokeDasharray = '100';
      this.progressIndicator.style.strokeDashoffset = '100';
      void this.progressIndicator.offsetWidth;
    }
    
    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.startProgressBar();
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    if (this.isFadeEffect()) {
      event.preventDefault();
      this.wasClicked = true;

      this.resetSlideshowProgress();

      if (this.autoplay) {
        clearInterval(this.autoplay);
        this.autoplay = null;
      }

      const direction = event.currentTarget.name;
      const increment = direction === 'next' ? 1 : -1;
      this.setFadePage(this.currentPage + increment);
      this.applyAnimationToAnnouncementBar(direction);

      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        setTimeout(() => {
          this.play();
        }, 50);
      }
      return;
    }

    super.onButtonClick(event);
    this.wasClicked = true;

    this.remainingMs = null;
    this.progressOffset = 100;
    if (this.progressIndicator) {
      this.progressIndicator.style.transition = 'none';
      this.progressIndicator.style.strokeDasharray = '100';
      this.progressIndicator.style.strokeDashoffset = '100';
      void this.progressIndicator.offsetWidth;
    }

    if (this.autoplay) {
      clearInterval(this.autoplay);
      this.autoplay = null;
    }

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        setTimeout(() => {
          this.play();
        }, 50);
      }
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);

    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      setTimeout(() => {
        this.play();
      }, 50);
    }
  }

  setSlidePosition(position) {
    if (this.isFadeEffect()) {
      const pageNumber = Math.round(position / this.sliderItemOffset) + 1;
      this.setFadePage(pageNumber);
      return;
    }

    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    if (this.isFadeEffect()) {
      if (!this.slider || !this.nextButton) return;

      this.currentPage = Math.min(
        Math.max(this.currentPage || 1, 1),
        this.totalPages || this.sliderItemsToShow.length || 1
      );

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      this.prevButton.removeAttribute('disabled');
      this.nextButton.removeAttribute('disabled');

      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
      this.sliderControlButtons.forEach((link) => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });

      if (this.sliderControlButtons[this.currentPage - 1]) {
        this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
        this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
      }

      this.updateFadeSlides();
      return;
    }

    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    this.isTogglingAutoplay = true;
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    
    if (this.autoplayButtonIsSetToPlay) {
      this.play();
    } else {
      this.pause();
    }
    
    this.togglePlayButtonState(!this.autoplayButtonIsSetToPlay);
    
    setTimeout(() => {
      this.isTogglingAutoplay = false;
    }, 100);
    
    if (this.sliderAutoplayButton && event) {
      setTimeout(() => {
        this.sliderAutoplayButton.focus();
      }, 0);
    }
  }

  focusOutHandling(event) {
    if (this.isTogglingAutoplay) {
      return;
    }
    
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      
      if (this.autoplayButtonIsSetToPlay && !focusedOnAutoplayButton && this.autoplay === null) {
        this.play();
      }
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        return;
      }
      if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  startProgressBar() {
    if (!this.progressIndicator || !this.sliderAutoplayButton || !this.autoplayButtonIsSetToPlay) {
      return;
    }

    this.progressIndicator.style.transition = 'none';
    void this.progressIndicator.offsetWidth;

    const duration = this.remainingMs != null ? this.remainingMs : this.autoplaySpeed;
    const startOffset = (this.remainingMs != null && this.progressOffset != null) ? this.progressOffset : 100;

    this.progressIndicator.style.willChange = 'stroke-dashoffset';
    this.progressIndicator.style.strokeDasharray = '100';
    this.progressIndicator.style.strokeDashoffset = String(startOffset);
    void this.progressIndicator.offsetWidth;

    setTimeout(() => {
      if (!this.autoplayButtonIsSetToPlay || !this.progressIndicator) {
        return;
      }
      this.progressIndicator.style.transition = `stroke-dashoffset ${duration}ms linear`;
      void this.progressIndicator.offsetWidth;
      this.progressIndicator.style.strokeDashoffset = '0';
    }, 10);

    if (this.remainingMs != null) {
      this.remainingMs = null;
    }
  }

  stopProgressBar() {
    if (!this.progressIndicator) {
      return;
    }

    const computed = window.getComputedStyle(this.progressIndicator);
    const currentOffset = parseFloat(computed.strokeDashoffset) || 100;
    const remaining = Math.max(0, Math.min(this.autoplaySpeed * (currentOffset / 100), this.autoplaySpeed));

    this.remainingMs = remaining;
    this.progressOffset = currentOffset;

    this.progressIndicator.style.transition = 'none';
    void this.progressIndicator.offsetWidth;
    this.progressIndicator.style.strokeDashoffset = String(currentOffset);
  }

  play() {
    if (this.sliderAutoplayButton && !this.autoplayButtonIsSetToPlay) {
      return;
    }
    
    if (this.isTogglingAutoplay && !this.autoplayButtonIsSetToPlay) {
      return;
    }
    
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplayGuard = false;
    
    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.startProgressBar();
    }
    
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
    this.autoplay = null;
    this.autoplayGuard = true;
    
    if (this.progressIndicator) {
      this.stopProgressBar();
    }
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    if (this.isFadeEffect()) {
      this.autoplayGuard = true;
      this.resetSlideshowProgress();
      this.setFadePage(this.currentPage + 1);
      this.applyAnimationToAnnouncementBar();

      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        setTimeout(() => {
          this.autoplayGuard = false;
          this.startProgressBar();
        }, 100);
      } else {
        this.autoplayGuard = false;
      }
      return;
    }

    this.autoplayGuard = true;
    
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.remainingMs = null;
    this.progressOffset = 100;
    if (this.progressIndicator) {
      this.progressIndicator.style.transition = 'none';
      this.progressIndicator.style.strokeDasharray = '100';
      this.progressIndicator.style.strokeDashoffset = '100';
      void this.progressIndicator.offsetWidth;
    }

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();

    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      setTimeout(() => {
        this.autoplayGuard = false;
        this.startProgressBar();
      }, 100);
    } else {
      this.autoplayGuard = false;
    }
  }

  setSlideVisibility(event) {
    if (this.isFadeEffect()) {
      this.updateFadeSlides();
    }

    // Check if page changed and reset progress bar if needed
    if (this.currentPage !== this.previousPage) {
      this.remainingMs = null;
      this.progressOffset = 100;
      if (this.progressIndicator) {
        this.progressIndicator.style.transition = 'none';
        this.progressIndicator.style.strokeDasharray = '100';
        this.progressIndicator.style.strokeDashoffset = '100';
        void this.progressIndicator.offsetWidth;
      }
      
      if (this.autoplay) {
        clearInterval(this.autoplay);
        this.autoplay = null;
      }
      
      // Restart autoplay if enabled
      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        setTimeout(() => {
          this.play();
        }, 50);
      }
      
      this.previousPage = this.currentPage;
    }
    
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();

    if (this.isFadeEffect()) {
      this.resetSlideshowProgress();

      if (this.autoplay) {
        clearInterval(this.autoplay);
        this.autoplay = null;
      }

      this.setFadePage(this.sliderControlLinksArray.indexOf(event.currentTarget) + 1);

      if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
        setTimeout(() => {
          this.play();
        }, 50);
      }
      return;
    }
    
    this.remainingMs = null;
    this.progressOffset = 100;
    if (this.progressIndicator) {
      this.progressIndicator.style.transition = 'none';
      this.progressIndicator.style.strokeDasharray = '100';
      this.progressIndicator.style.strokeDashoffset = '100';
      void this.progressIndicator.offsetWidth;
    }
    
    if (this.autoplay) {
      clearInterval(this.autoplay);
      this.autoplay = null;
    }
    
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
    
    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      setTimeout(() => {
        this.play();
      }, 50);
    }
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class XBloomSlider extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[id^="XBloomSliderInner-"]');
    this.sliderItems = Array.from(this.querySelectorAll('.slideshow__slide'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotContainer = this.querySelector('.slideshow__control-wrapper');
    this.dots = Array.from(this.dotContainer?.querySelectorAll('.slider-counter__link') || []);

    if (!this.slider || !this.prevButton || !this.nextButton) return;

    const parsedNav = parseInt(this.getAttribute('data-slides-per-nav') || '1', 10);
    this.slidesPerNav = Number.isFinite(parsedNav) && parsedNav > 0 ? Math.floor(parsedNav) : 1;

    const n = this.sliderItems.length;
    this.totalPages =
      this.slidesPerNav > 1 ? Math.max(1, Math.ceil(n / this.slidesPerNav)) : Math.max(1, n);

    this.currentPage = 1;
    this.sliderItemOffset = this.sliderItems[0]?.offsetWidth || 0;

    this.isManualScroll = false; // 点击或按钮标记

    this.updateControls();

    // 按钮事件
    this.prevButton.addEventListener('click', () => this.goToSlide(this.currentPage - 1));
    this.nextButton.addEventListener('click', () => this.goToSlide(this.currentPage + 1));

    // dot 点击事件
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index + 1));
    });

    this.onScroll = () => {
      if (this.isManualScroll) return;
      const page = this.getPageFromScrollLeft(this.slider.scrollLeft);
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.updateControls();
      }
    };
    this.slider.addEventListener('scroll', this.onScroll);

    // ResizeObserver，确保宽度变化时 dot 和按钮状态正确
    this.resizeObserver = new ResizeObserver(() => {
      this.sliderItemOffset = this.sliderItems[0]?.offsetWidth || 0;
      this.goToSlide(this.currentPage);
    });
    this.resizeObserver.observe(this.slider);
  }

  getScrollLeftForPage(page) {
    const p = Math.max(1, Math.min(page, this.totalPages));
    const startIndex = (p - 1) * this.slidesPerNav;
    const items = this.sliderItems;
    if (!items.length) return 0;
    const idx = Math.min(startIndex, items.length - 1);
    const left = items[idx].offsetLeft;
    const maxLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    return Math.min(left, maxLeft);
  }

  getPageFromScrollLeft(scrollLeft) {
    let best = 1;
    let bestDiff = Infinity;
    for (let p = 1; p <= this.totalPages; p++) {
      const target = this.getScrollLeftForPage(p);
      const diff = Math.abs(scrollLeft - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = p;
      }
    }
    return best;
  }

  goToSlide(page) {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    const left = this.getScrollLeftForPage(this.currentPage);

    this.isManualScroll = true; // 标记手动触发
    this.slider.scrollTo({ left, behavior: 'smooth' });

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isManualScroll = false;
      this.updateControls();
    }, 500);

    this.updateControls();
  }

  updateControls() {
    this.prevButton.disabled = this.currentPage === 1;
    this.nextButton.disabled = this.currentPage === this.totalPages;

    this.dots.forEach((dot, index) => {
      const dotSpan = dot.querySelector('span.dot');
      if (index === this.currentPage - 1) {
        dot.classList.add('slider-counter__link--active');
        dot.setAttribute('aria-current', 'true');
        dotSpan?.classList.add('active');
      } else {
        dot.classList.remove('slider-counter__link--active');
        dot.removeAttribute('aria-current');
        dotSpan?.classList.remove('active');
      }
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.slider && this.onScroll) {
      this.slider.removeEventListener('scroll', this.onScroll);
    }
  }
}

customElements.define('xbloom-slider', XBloomSlider);

class XBloomSliderSwiper extends HTMLElement {
  connectedCallback() {
    // 等待 DOM 完全渲染后再初始化 Swiper
    setTimeout(() => {
      this.initSwiper();
    }, 0);
  }

  initSwiper() {
    if (!window.XBLOOM3 || !window.XBLOOM3.Swiper) {
      console.warn('Swiper not loaded');
      return;
    }

    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) {
      console.warn('Swiper container not found');
      return;
    }

    // 检查是否已经初始化过
    if (swiperContainer.swiper) {
      return;
    }

    // 获取配置选项
    const slidesPerView = this.getAttribute('slides-per-view') || '1';
    const spaceBetween = parseInt(this.getAttribute('space-between') || '0');
    const spaceBetweenMobile = this.getAttribute('space-between-mobile') 
      ? parseInt(this.getAttribute('space-between-mobile')) 
      : spaceBetween;
    const loop = this.getAttribute('loop') === 'true';
    const autoplay = this.getAttribute('autoplay') === 'true';
    const pagination = this.getAttribute('pagination') === 'true';
    const navigation = this.getAttribute('navigation') === 'true';

    // 判断是否为移动端
    const isMobile = window.XBLOOM3?.IS_MOBILE || window.innerWidth < 750;
    
    const config = {
      slidesPerView: parseFloat(slidesPerView),
      spaceBetween: isMobile ? spaceBetweenMobile : spaceBetween,
      loop: loop,
      grabCursor: true,
      mousewheel: {
        forceToAxis: true,
      },
    };

    // 可选的自动播放
    if (autoplay) {
      config.autoplay = {
        delay: parseInt(this.getAttribute('autoplay-delay') || '3000'),
        disableOnInteraction: false,
      };
    }

    // 可选的分页
    if (pagination) {
      const paginationEl = this.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }

    // 可选的导航按钮
    if (navigation) {
      const prevEl = this.querySelector('.swiper-button-prev');
      const nextEl = this.querySelector('.swiper-button-next');
      if (prevEl && nextEl) {
        config.navigation = {
          prevEl: prevEl,
          nextEl: nextEl,
        };
      }
    }

    // 初始化 Swiper
    this.swiperInstance = new window.XBLOOM3.Swiper(swiperContainer, config);
  }

  disconnectedCallback() {
    // 清理 Swiper 实例
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

customElements.define('xbloom-slider-swiper', XBloomSliderSwiper);


class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {});
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class HorizontalScroll {
  constructor(options = {}) {
    this.containerSelector = options.containerSelector || '.horizontal-scroll';
    this.itemSelector = options.itemSelector || '.horizontal-scroll__item';
    this.activeClass = options.activeClass || 'active';
    this.mobileBreakpoint = options.mobileBreakpoint || 989;
    this.behavior = options.behavior || 'smooth';
    this.allowLinkNavigation = options.allowLinkNavigation || false;
    this.bindClickEvents = options.bindClickEvents !== false;
    
    this.container = document.querySelector(this.containerSelector);
    this.items = Array.from(document.querySelectorAll(this.itemSelector));
    
    this.init();
  }

  clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  computeTargetScrollLeft(item) {
    if (!this.container || !item) return 0;
    
    const containerRect = this.container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const currentScrollLeft = this.container.scrollLeft;
    
    const itemLeftInContent = (itemRect.left - containerRect.left) + currentScrollLeft;
    const itemCenterInContent = itemLeftInContent + (itemRect.width / 2);
    
    const target = itemCenterInContent - (this.container.clientWidth / 2);
    const max = Math.max(0, this.container.scrollWidth - this.container.clientWidth);
    
    return this.clamp(Math.round(target), 0, max);
  }

  scrollToItem(item, behavior = this.behavior) {
    if (!this.container || !item) return;
    
    if (this.container.scrollWidth <= this.container.clientWidth) return;
    
    const target = this.computeTargetScrollLeft(item);
    
    try {
      this.container.scrollTo({ left: target, behavior });
    } catch (e) {
      this.container.scrollLeft = target;
    }
  }

  setActiveItem(item) {
    if (!item) return;
    
    this.items.forEach(i => i.classList.remove(this.activeClass));
    
    item.classList.add(this.activeClass);
    
    this.scrollToItem(item);
  }

  ensureMobileScrollableStyles() {
    if (!this.container) return;
    
    if (window.innerWidth <= this.mobileBreakpoint) {
      const style = getComputedStyle(this.container);
      
      if (style.overflowX !== 'auto' && style.overflowX !== 'scroll') {
        this.container.style.overflowX = 'auto';
        this.container.style.webkitOverflowScrolling = 'touch';
      }
      
      Array.from(this.container.children).forEach(child => {
        if (!child.style.flex) child.style.flex = '0 0 auto';
        if (!child.style.whiteSpace) child.style.whiteSpace = 'nowrap';
      });
    }
  }

  handleItemClick() {
    this.items.forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') {
          if (this.allowLinkNavigation) {
            const targetItem = e.target.closest('li') || item;
            this.setActiveItem(targetItem);
            
            setTimeout(() => {
              if (e.target.href) {
                window.location.href = e.target.href;
              }
            }, 300);
          } else {
            e.preventDefault();
            e.stopPropagation();
            
            const targetItem = e.target.closest('li') || item;
            this.setActiveItem(targetItem);
          }
        } else {
          e.preventDefault();
          this.setActiveItem(item);
        }
      });
    });
  }

  init() {
    if (!this.container || !this.items.length) {
      return;
    }

    this.ensureMobileScrollableStyles();
    
    if (this.bindClickEvents) {
      this.handleItemClick();
    }

    window.addEventListener('resize', () => {
      this.items = Array.from(document.querySelectorAll(this.itemSelector));
      this.ensureMobileScrollableStyles();
    });
  }

  destroy() {
    this.items.forEach(item => {
      item.removeEventListener('click', this.handleItemClick);
    });
    
    window.removeEventListener('resize', this.ensureMobileScrollableStyles);
  }
}

window.HorizontalScroll = HorizontalScroll;

// Enhanced Scroll Navigation with Anchor Support
class ScrollNavigation {
  constructor(options = {}) {
    this.containerSelector = options.containerSelector || '.scroll_nav-sticky';
    this.itemSelector = options.itemSelector || '.scroll_nav_item';
    this.activeClass = options.activeClass || 'active';
    this.mobileBreakpoint = options.mobileBreakpoint || 750;
    this.scrollOffset = options.scrollOffset || 100;
    this.smoothScroll = options.smoothScroll !== false;
    
    this.container = document.querySelector(this.containerSelector);
    this.items = Array.from(document.querySelectorAll(this.itemSelector));
    this.sections = new Map();
    
    this.init();
  }

  getSectionId(navText) {
    const textMap = {
      'Overview': 'overview',
      'Story': 'story', 
      'Roaster': 'roaster',
      'FAQ & Reviews': 'faq-reviews',
      'In the box': 'in_the_box',
      'FAQ': 'faq-reviews',
      'Reviews': 'faq-reviews'
    };
    
    if (textMap[navText]) {
      return textMap[navText];
    }
    
    const lowerText = navText.toLowerCase();
    for (const [key, value] of Object.entries(textMap)) {
      if (lowerText.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerText)) {
        return value;
      }
    }
    
    return null;
  }

  scrollToSection(sectionId, behavior = 'smooth') {
    const targetElement = document.getElementById(sectionId);
    if (!targetElement) {
      return;
    }

    const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - this.scrollOffset;
    
    if (this.smoothScroll && behavior === 'smooth') {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, targetPosition);
    }
  }

  setActiveItem(item) {
    if (!item) return;
    
    this.items.forEach(i => i.classList.remove(this.activeClass));
    item.classList.add(this.activeClass);
    
    this.scrollToCenterItem(item);
  }

  scrollToCenterItem(item) {
    if (!this.container || !item) return;
    
    const containerRect = this.container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const currentScrollLeft = this.container.scrollLeft;
    
    const itemLeftInContent = (itemRect.left - containerRect.left) + currentScrollLeft;
    const itemCenterInContent = itemLeftInContent + (itemRect.width / 2);
    
    const target = itemCenterInContent - (this.container.clientWidth / 2);
    const max = Math.max(0, this.container.scrollWidth - this.container.clientWidth);
    
    const clampedTarget = Math.max(0, Math.min(max, Math.round(target)));
    
    try {
      this.container.scrollTo({ left: clampedTarget, behavior: 'smooth' });
    } catch (e) {
      this.container.scrollLeft = clampedTarget;
    }
  }

  handleItemClick() {
    this.items.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const navText = item.textContent.trim();
        const sectionId = this.getSectionId(navText);
        
        if (sectionId) {
          this.setActiveItem(item);
          this.scrollToSection(sectionId);
        }
      });
    });
  }

  updateActiveOnScroll() {
    const scrollPosition = window.pageYOffset + this.scrollOffset + 50;
    
    let activeSection = null;
    let minDistance = Infinity;
    
    for (const [navText, sectionId] of this.sections) {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + window.pageYOffset;
        const distance = Math.abs(scrollPosition - elementTop);
        
        if (distance < minDistance && rect.top <= this.scrollOffset + 100) {
          minDistance = distance;
          activeSection = sectionId;
        }
      }
    }
    
    if (activeSection) {
      const activeItem = this.items.find(item => {
        const navText = item.textContent.trim();
        return this.getSectionId(navText) === activeSection;
      });
      
      if (activeItem && !activeItem.classList.contains(this.activeClass)) {
        this.setActiveItem(activeItem);
      }
    }
  }

  init() {
    if (!this.container || !this.items.length) {
      return;
    }

    this.items.forEach(item => {
      const navText = item.textContent.trim();
      const sectionId = this.getSectionId(navText);
      if (sectionId) {
        this.sections.set(navText, sectionId);
      }
    });

    this.handleItemClick();
    
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        this.updateActiveOnScroll();
      }, 10);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });

    setTimeout(() => {
      this.updateActiveOnScroll();
    }, 100);
    
    const handleResize = () => {
      this.items = Array.from(document.querySelectorAll(this.itemSelector));
      this.updateActiveOnScroll();
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const navItem = this.items.find(item => {
          const navText = item.textContent.trim();
          return this.getSectionId(navText) === hash;
        });
        
        if (navItem) {
          this.setActiveItem(navItem);
          this.scrollToSection(hash, 'auto');
        }
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    this._handleHashChange = handleHashChange;
    
    handleHashChange();
    
    this._handleScroll = handleScroll;
    this._handleResize = handleResize;
  }

  scrollTo(navText) {
    const sectionId = this.getSectionId(navText);
    if (sectionId) {
      this.scrollToSection(sectionId);
      
      const item = this.items.find(item => item.textContent.trim() === navText);
      if (item) {
        this.setActiveItem(item);
      }
    }
  }

  destroy() {
    this.items.forEach(item => {
      item.removeEventListener('click', this.handleItemClick);
    });
    
    if (this._handleScroll) {
      window.removeEventListener('scroll', this._handleScroll);
    }
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize);
    }
    if (this._handleHashChange) {
      window.removeEventListener('hashchange', this._handleHashChange);
    }
  }
}

window.ScrollNavigation = ScrollNavigation;

document.addEventListener('DOMContentLoaded', function() {  
  const collectionLinksContainer = document.querySelector('.collection-links');
  if (collectionLinksContainer) {
    new HorizontalScroll({
      containerSelector: '.collection-links',
      itemSelector: '.collection-link',
      activeClass: 'active',
      mobileBreakpoint: 750,
      allowLinkNavigation: true 
    });
  }

  const scrollNavContainer = document.querySelector('.scroll_nav-sticky');
  if (scrollNavContainer) {
    new ScrollNavigation({
      containerSelector: '.scroll_nav-sticky',
      itemSelector: '.scroll_nav_item',
      activeClass: 'active',
      mobileBreakpoint: 750,
      scrollOffset: 120,
      smoothScroll: true
    });
  }
});



  (function() {
    
    function initializeCardOverlays() {
      // Handle cards in .recommendations_ul_container
      const recommendationsContainer = document.querySelector('.recommendations_ul_container');
      
      if (recommendationsContainer) {
        const liElements = recommendationsContainer.querySelectorAll('li');
        
        liElements.forEach(function(li, index) {
          const cardInner = li.querySelector('.card-product-add-inner');
          const cardOverlay = li.querySelector('.card-panel-overlay');
          const closeButton = li.querySelector('.xbloom-drawer__close-button');

          if (cardInner && cardOverlay) {
            cardInner.addEventListener('click', function(e) {
              e.stopPropagation();
              
              liElements.forEach(function(otherLi) {
                const otherOverlay = otherLi.querySelector('.card-panel-overlay');
                if (otherOverlay && otherOverlay !== cardOverlay) {
                  otherOverlay.classList.remove('active');
                }
              });
              
              cardOverlay.classList.add('active');
            });
          }
          
          if (closeButton && cardOverlay) {
            closeButton.addEventListener('click', function(e) {
              e.stopPropagation();
              cardOverlay.classList.remove('active');
            });
          }
        });
      }
      
      // Handle cards in xbloom-slider
      const xbloomSliders = document.querySelectorAll('xbloom-slider');
      
      xbloomSliders.forEach(function(slider) {
        const slideElements = slider.querySelectorAll('.grid__item.slideshow__slide');
        
        slideElements.forEach(function(slide, index) {
          // Skip if already initialized
          if (slide.hasAttribute('data-overlay-initialized')) {
            return;
          }
          
          const cardInner = slide.querySelector('.card-product-add-inner');
          const cardOverlay = slide.querySelector('.card-panel-overlay');
          const closeButton = slide.querySelector('.xbloom-drawer__close-button');

          if (cardInner && cardOverlay) {
            // Mark as initialized to avoid duplicate listeners
            slide.setAttribute('data-overlay-initialized', 'true');
            
            cardInner.addEventListener('click', function(e) {
              e.stopPropagation();
              
              // Close all other overlays
              const allOverlays = document.querySelectorAll('.card-panel-overlay');
              allOverlays.forEach(function(overlay) {
                if (overlay !== cardOverlay) {
                  overlay.classList.remove('active');
                }
              });
              
              cardOverlay.classList.add('active');
            });
          }
          
          if (closeButton && cardOverlay) {
            closeButton.addEventListener('click', function(e) {
              e.stopPropagation();
              cardOverlay.classList.remove('active');
            });
          }
        });
      });
    }
    
    document.addEventListener('DOMContentLoaded', function() {
      initializeCardOverlays();
    });
    
    setTimeout(function() {
      initializeCardOverlays();
    }, 2000);
    
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          const recommendationsContainer = document.querySelector('.recommendations_ul_container');
          const xbloomSliders = document.querySelectorAll('xbloom-slider');
          
          let shouldReinitialize = false;
          
          if (recommendationsContainer && !recommendationsContainer.hasAttribute('data-overlay-initialized')) {
            recommendationsContainer.setAttribute('data-overlay-initialized', 'true');
            shouldReinitialize = true;
          }
          
          xbloomSliders.forEach(function(slider) {
            if (!slider.hasAttribute('data-overlay-initialized')) {
              slider.setAttribute('data-overlay-initialized', 'true');
              shouldReinitialize = true;
            }
          });
          
          if (shouldReinitialize) {
            initializeCardOverlays();
          }
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
      document.addEventListener('click', function(e) {
      const cardInner = e.target.closest('.card-product-add-inner');
      if (cardInner) {
        e.preventDefault();
        e.stopPropagation();
        
        // Support both li (recommendations) and div.grid__item (xbloom-slider)
        const container = cardInner.closest('li, .grid__item.slideshow__slide');
        if (container) {
          const cardOverlay = container.querySelector('.card-panel-overlay');
          if (cardOverlay) {
            const allOverlays = document.querySelectorAll('.card-panel-overlay');
            allOverlays.forEach(function(overlay) {
              if (overlay !== cardOverlay) {
                overlay.classList.remove('active');
              }
            });
            
            cardOverlay.classList.add('active');
          }
        }
      }
      
      const closeButton = e.target.closest('.xbloom-drawer__close-button');
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        
        const cardOverlay = closeButton.closest('.card-panel-overlay');
        if (cardOverlay) {
          cardOverlay.classList.remove('active');
        }
      }
    });
  })();


